import React, { useContext, useEffect, useState } from 'react';
import { ServiceResponse } from '../../app/services/shared/interfaces';
import Breadcrumbs, { BreadcrumbsItem } from '../../template/MainTheme/components/Breadcrumbs';
import DefaultCard from '../../components/default/DefaultCard';
import DefaultModal from '../../components/default/DefaultModal';
import DefaultToolBar from '../../components/default/DefaultToolBar';
import ButtonCreate from '../../components/buttons/ButtonCreate';
import { toast } from 'react-toastify';
import useReactConfirmAlert from '../../hooks/useReactConfirmAlert';
import { AppContext } from '../../contexts/AppContext';
import usePaymentConditionService from '../../app/services/hooks/usePaymentConditionService';
import { PaymentCondition, PaymentConditionFilters } from '../../app/models/PaymentCondition';
import PaymentConditionDataTable from './components/tables/PaymentConditionDataTable';
import PaymentConditionCreate from './components/PaymentConditionCreate';
import PaymentConditionEdit from './components/PaymentConditionEdit';
import useNavigationPage from '../../hooks/useNavigationPage';

const breadcrumbs: BreadcrumbsItem[] = [
    {
        name: 'Condiciones de Pago',
        url: '/payment-conditions',
        isActive: true
    }
];

const PaymentConditions = () => {
    const { showLoading, hideLoading } = useContext(AppContext);
    const {
        fetchingGetPaymentConditions,
        getPaymentConditions,
        deletePaymentCondition
    } = usePaymentConditionService();
    const [paymentConditions, setPaymentConditions] = useState<PaymentCondition[]>([]);
    const { navigationPage } = useNavigationPage();

    const [paymentConditionIdEdit, setPaymentConditionIdEdit] = useState<number>(-1);
    const [showingEdit, setShowingEdit] = useState(false);
    const [showingCreate, setShowingCreate] = useState(false);
    const [totalRows, setTotalRows] = useState<number>(0);

    const [paymentConditionFilter, setPaymentConditionFilter] =
        useState<PaymentConditionFilters>({
            page: 1,
            per_page: 10,
            sort: 'id',
            order: 'desc',
            name: '',
            is_active: ''
        });

    useEffect(() => {
        reloadTable();
    }, [paymentConditionFilter]);

    const reloadTable = () => {
        getPaymentConditions(paymentConditionFilter, {
            onSuccess: (response: ServiceResponse) => {
                setPaymentConditions(response.data.payment_conditions);
                setTotalRows(response.data.total_rows);
            },
            onError: (response: ServiceResponse) => {
                toast.error(response.message, {
                    autoClose: 2000
                });
                if (response.data?.to_dashboard) {
                    navigationPage('/');
                }
            }
        });
    };

    const showEdit = (paymentConditionId: number) => {
        setShowingEdit(true);
        setPaymentConditionIdEdit(paymentConditionId);
    };

    const hideEdit = () => {
        setShowingEdit(false);
        setPaymentConditionIdEdit(-1);
    };

    const showCreate = () => {
        setShowingCreate(true);
    };

    const hideCreate = () => {
        setShowingCreate(false);
    };

    const destroy = (paymentConditionId: number) => {
        const _text =
            'Está a punto de eliminar la condición de pago #' + paymentConditionId;

        useReactConfirmAlert().requestConfirmation({
            title: '¿Estás seguro?',
            message: _text,
            buttons: {
                confirmButton: {
                    label: 'Sí, confirmar',
                    onClick: () => {
                        if (showLoading) showLoading('loading', 'Eliminando condición de pago...');

                        deletePaymentCondition(paymentConditionId, {
                            onSuccess: (response: ServiceResponse) => {
                                if (hideLoading) hideLoading();
                                useReactConfirmAlert().successAlert({
                                    title: 'Éxito',
                                    message: response.message
                                });
                                reloadTable();
                            },
                            onError: (response: ServiceResponse) => {
                                if (hideLoading) hideLoading();
                                useReactConfirmAlert().errorAlert({
                                    title: 'Error',
                                    message: response.message
                                });
                            }
                        });
                    }
                },
                cancelButton: {
                    label: 'No, cancelar',
                    onClick: () => {
                        setTimeout(() => {
                            useReactConfirmAlert().infoAlert({
                                title: 'Cancelado',
                                message: 'La condición de pago no se ha eliminado.'
                            });
                        }, 0);
                    }
                }
            }
        });
    };

    return (
        <>
            <Breadcrumbs pageSection="Condiciones de Pago" breadcrumbs={breadcrumbs} />

            <DefaultCard>
                <DefaultToolBar
                    left={
                        <ButtonCreate
                            callbackCreate={showCreate}
                            title="Nueva Condición de Pago"
                        />
                    }
                />
                <PaymentConditionDataTable
                    paymentConditions={paymentConditions}
                    edit={showEdit}
                    destroy={destroy}
                    loading={fetchingGetPaymentConditions}
                    filter={paymentConditionFilter}
                    setFilter={setPaymentConditionFilter}
                    totalRows={totalRows}
                    callbackSuccess={reloadTable}
                />
            </DefaultCard>

            {showingCreate ? (
                <DefaultModal
                    show={showingCreate}
                    handleClose={hideCreate}
                    title="Crear Condición de Pago"
                    backdrop={true}
                    showFooter={false}
                >
                    <PaymentConditionCreate
                        onSaved={() => {
                            reloadTable();
                            hideCreate();
                        }}
                        onCancel={hideCreate}
                    />
                </DefaultModal>
            ) : null}

            {showingEdit ? (
                <DefaultModal
                    show={showingEdit}
                    handleClose={hideEdit}
                    title="Editar Condición de Pago"
                    backdrop={true}
                    showFooter={false}
                >
                    <PaymentConditionEdit
                        paymentConditionId={paymentConditionIdEdit}
                        onSaved={() => {
                            reloadTable();
                            hideEdit();
                        }}
                        onCancel={hideEdit}
                        onError={hideEdit}
                    />
                </DefaultModal>
            ) : null}
        </>
    );
};

export default PaymentConditions;
